import type * as Phaser from 'phaser';
import type { WaterAppearance } from '../sim/environment/water.ts';
import { gridLineStarts } from '../sim/grid.ts';
import {
  GRID_SCREEN_PX,
  GRID_STROKE_ALPHA,
  GRID_STROKE_COLOR,
  cameraWorldRect,
  type WorldRect,
} from './view.ts';

function currentWorldRect(cam: Phaser.Cameras.Scene2D.Camera): WorldRect {
  return cameraWorldRect(cam.scrollX, cam.scrollY, cam.width, cam.height, cam.zoom);
}

function visibleMapRect(
  view: WorldRect,
  bounds: { width: number; height: number },
  pad = 0,
): WorldRect | null {
  const x = Math.max(0, view.x - pad);
  const y = Math.max(0, view.y - pad);
  const right = Math.min(bounds.width, view.x + view.width + pad);
  const bottom = Math.min(bounds.height, view.y + view.height + pad);

  if (right <= x || bottom <= y) {
    return null;
  }

  return { x, y, width: right - x, height: bottom - y };
}

/**
 * Paints the visible map rectangle as the seafloor and a screen-fixed grid clipped to that
 * rectangle. The camera background is the void, so anything the grid does not fill reads as
 * outside the map. The translucent water sheet is a separate layer so reefs and
 * shallow beds can sit between the two.
 */
export function drawViewportGrid(
  gfx: Phaser.GameObjects.Graphics,
  cam: Phaser.Cameras.Scene2D.Camera,
  bounds: { width: number; height: number },
  seafloor: number,
  strokeColor = GRID_STROKE_COLOR,
  strokeAlpha = GRID_STROKE_ALPHA,
): void {
  const zoom = cam.zoom;

  gfx.clear();

  if (zoom <= 0) {
    return;
  }

  const view = currentWorldRect(cam);
  const cellWorld = GRID_SCREEN_PX / zoom;
  const floor = visibleMapRect(view, bounds, cellWorld);

  if (floor) {
    gfx.fillStyle(seafloor, 1);
    gfx.fillRect(floor.x, floor.y, floor.width, floor.height);
  }

  const minX = Math.max(0, view.x - cellWorld);
  const maxX = Math.min(bounds.width, view.x + view.width + cellWorld);
  const minY = Math.max(0, view.y - cellWorld);
  const maxY = Math.min(bounds.height, view.y + view.height + cellWorld);

  if (maxX <= minX || maxY <= minY) {
    return;
  }

  gfx.lineStyle(1 / zoom, strokeColor, strokeAlpha);

  for (const worldX of gridLineStarts(minX, maxX, cellWorld)) {
    gfx.lineBetween(worldX, minY, worldX, maxY);
  }

  for (const worldY of gridLineStarts(minY, maxY, cellWorld)) {
    gfx.lineBetween(minX, worldY, maxX, worldY);
  }
}

/** Translucent open water over reefs. Alpha falls as the sun climbs so the bed shows through. */
export function drawWaterSheet(
  gfx: Phaser.GameObjects.Graphics,
  cam: Phaser.Cameras.Scene2D.Camera,
  bounds: { width: number; height: number },
  water: WaterAppearance,
): void {
  gfx.clear();

  if (cam.zoom <= 0) {
    return;
  }

  const rect = visibleMapRect(currentWorldRect(cam), bounds, GRID_SCREEN_PX / cam.zoom);

  if (!rect) {
    return;
  }

  gfx.fillStyle(water.color, water.alpha);
  gfx.fillRect(rect.x, rect.y, rect.width, rect.height);
}
